import { AddOnType, BillingStatus, ExpirationFilterOption, SpecialTerm, StorageFilterOption, SubscriptionTier } from '../enums/clientEnums';
import type {
  ClientAdmin,
  ClientDetailAddOn,
  ClientDetails,
  ClientPaymentInfo,
  ClientSubscription,
  ClientSummary,
} from '../models/clientManager/clientDetails.model';
import type { ClientListItem, ClientsListResponse, PaymentOrganizationModel } from '../models/clientManager/clientList.model';

export type { ClientDetails, ClientSummary, ClientAdmin, ClientSubscription, ClientDetailAddOn, ClientPaymentInfo };
export type { ClientListItem, PaymentOrganizationModel, ClientsListResponse };

export interface ClientAddOn {
  /** The assignment's own id; `addonId` is its catalogue entry. */
  id: number;
  addonId: number;
  name: string;
  addonType: AddOnType | string;
  /** ISO date string; null = no expiry. */
  expiration: string | null;
  cost: number;
  status: BillingStatus;
}

export interface ClientRecord {
  id: number;
  name: string;
  adminEmail: string;
  subscriptionTier: SubscriptionTier;
  /** ISO date string; null = no expiry. */
  expiration: string | null;
  storageUsedGb: number;
  /** Plan allowance plus active storage add-ons; other categories grant none. */
  storageTotalGb: number;
  cost: number;
  status: BillingStatus;
  /** Decides the Archive group a row lands in and whether its ⋮ menu offers Archive or Restore. */
  archived: boolean;
  hasAddons: boolean;
}

export interface ClientGroup {
  id: number;
  name: string;
  clients: ClientRecord[];
}

export interface ClientGroupsPage {
  groups: ClientGroup[];
  /** Archived clients of this page, lifted out of their payment groups; merged across pages into one group. */
  archivedClients: ClientRecord[];
  /** Offset to request next; null once the API stops reporting more. */
  nextOffset: number | null;
}

// FILTERS panel selection. Every section but the query itself is inert until the
// backend accepts filter parameters; the selection is kept whole so wiring it is
// a service change alone.
export interface ClientFilterSelection {
  organisations: string[];
  subscriptions: SubscriptionTier[];
  addOns: string[];
  expirationStatuses: ExpirationFilterOption[];
  /** ISO 'YYYY-MM-DD'; '' = unset. */
  expirationDate: string;
  storageMetrics: StorageFilterOption[];
  /** [from, to] in GB; '' = unset end. */
  storageRange: [string, string];
}

export const EMPTY_CLIENT_FILTER_SELECTION: ClientFilterSelection = {
  organisations: [],
  subscriptions: [],
  addOns: [],
  expirationStatuses: [],
  expirationDate: '',
  storageMetrics: [],
  storageRange: ['', ''],
};

export const isClientFilterSelectionActive = (selection: ClientFilterSelection): boolean =>
  selection.organisations.length > 0 ||
  selection.subscriptions.length > 0 ||
  selection.addOns.length > 0 ||
  selection.expirationStatuses.length > 0 ||
  selection.expirationDate !== '' ||
  selection.storageMetrics.length > 0 ||
  selection.storageRange[0] !== '' ||
  selection.storageRange[1] !== '';

// The add-on catalogue as GET /admin/addons returns it.
export interface AddOnCatalogueItem {
  id: number;
  addonType: AddOnType | string;
  name: string;
  description: string;
  /** The allowance the add-on grants; storage counts GBs, the rest count seats. */
  quantity: number;
  price: number;
  /** False for storage, which has no length of its own and so has no length control. */
  hasExpiry: boolean;
  /** Null = unlimited; a reached limit drops the add-on from the picker. */
  maxQuantity: number | null;
}

export interface AddOnsResponse {
  addons: AddOnCatalogueItem[];
}

// A plan as GET /admin/subscriptions/types returns it; `id` is the tier's wire value.
export interface SubscriptionType {
  id: SubscriptionTier;
  name: string;
  price: number;
  features: {
    maps: number;
    storageGB: number;
    admins: number;
    editors: number;
    viewers: number | string;
  };
}

export interface SubscriptionTypesResponse {
  subscriptionTypes: SubscriptionType[];
}

// The organization a client is billed through; names are unique backend-side.
export interface PaymentGroup {
  id: number;
  name: string;
}

export interface PaymentGroupsResponse {
  paymentGroups: PaymentGroup[];
}

export type YearTerm = '1' | '2' | '3' | '4' | '5';

/** A length token: whole years, the two-week demo, or the client's existing expiry. */
export type SubscriptionTerm = YearTerm | SpecialTerm;

export interface ClientFormAddOn {
  addonId: number;
  /** Fixed at 1 for now; the payload carries it, so multiples are a data change rather than a UI one. */
  quantity: number;
  /** What the user picked; null for storage and for a saved row left untouched. */
  term: SubscriptionTerm | null;
  /** The count the client already holds (0 for a new row); display-only, never sent. */
  heldQuantity: number;
  /** Only an expired add-on may be lowered or removed; display-only, never sent. */
  isExpired: boolean;
  /** The saved length in whole years, resent as received; null for storage or an until-subscription-end add-on. */
  heldYears: number | null;
  /** The saved expiry; with `heldYears` it gives the add-on's own start for extensions. */
  expiresAt: string | null;
  /** True once the user picks a length on an expired add-on; only then does the item carry `renew`. */
  renew: boolean;
}

// The create/edit wizard's form state, covering all steps.
export interface ClientFormValues {
  name: string;
  adminName: string;
  adminEmail: string;
  adminPhone: string;
  subscriptionType: SubscriptionTier | '';
  subscriptionTerm: SubscriptionTerm | '';
  addOns: ClientFormAddOn[];
  /** Null while the name is one the user typed; the group is created on submit. */
  paymentGroupId: number | null;
  paymentGroupName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  paymentMethod: string;
  markAsPaid: boolean;
}

// Names a payment group one way, never both.
export type PaymentGroupSelection = { paymentGroupId: number; paymentGroupName?: never } | { paymentGroupName: string; paymentGroupId?: never };

interface ClientPaymentDetails {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  paymentMethod: string;
  markAsPaid: boolean;
}

export interface ClientPayload {
  client: {
    clientName: string;
    administratorName: string;
    administratorEmail: string;
    administratorPhone: string;
  };
  subscription: {
    subscriptionType: SubscriptionTier | '';
    /** Whole years; null for terms the backend derives itself (demo, unchanged expiry). */
    subscriptionLength: number | null;
    addons: { addonId: number; quantity: number; addonLength?: number; untilSubscriptionEnd?: true; renew?: true }[];
  };
  payment: PaymentGroupSelection & ClientPaymentDetails;
}

export interface ClientUpdatePayload {
  client?: Partial<ClientPayload['client']>;
  subscription?: ClientPayload['subscription'];
  // paymentGroupId/paymentGroupName: 0 or 1 of the pair -- omitted entirely when unchanged.
  payment?: Partial<PaymentGroupSelection> & Partial<ClientPaymentDetails>;
}

export interface ClientPaymentGroupResult {
  id: number;
  name: string;
  /** False when the name/id resolved to an already-existing group instead of creating one. */
  created: boolean;
}

export interface CreateClientResponse {
  clientId: number;
  clientName: string;
  admin: { id: number; email: string; status: string };
  message: string;
  paymentGroup: ClientPaymentGroupResult;
}

export interface UpdateClientResponse {
  message: string;
  clientId: number;
  /** Only present when the request actually touched the payment group. */
  paymentGroup?: ClientPaymentGroupResult;
}

export interface ArchiveClientResponse {
  message: string;
  clientId: number;
  archived: boolean;
}

export interface DeleteClientResponse {
  message: string;
  clientId: number;
  deletedAt: string;
}
